import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faEnvelope, faPhone, faCalendarAlt, faMapMarkerAlt,
  faBuilding, faBriefcase, faIdCard, faMoneyBillWave, faCheckCircle,
  faExclamationTriangle, faSpinner, faTimes, faAddressCard,
  faStethoscope, faChild, faUserTie, faGlobe, faImage
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_URL = `${API_CONFIG2.apiUrl}employe/api_employe.php`;

const EmployeFormModal = ({
  show,
  onHide,
  onSuccess,
  employeId = null,
  initialData = null,
  readOnly = false
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    postnom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    nationalite: 'Congolaise',
    sexe: '',
    etat_civil: 'celibataire',
    nombre_enfants: 0,
    telephone: '',
    telephone2: '',
    email: '',
    province: '',
    ville: '',
    commune: '',
    quartier: '',
    adresse: '',
    photo: 'default.png',
    personne_urgence: '',
    telephone_urgence: '',
    lien_parente: '',
    date_embauche: '',
    date_depart: '',
    service: '',
    departement: '',
    fonction: '',
    categorie_professionnelle: '',
    niveau_etude: '',
    type_contrat: 'CDI',
    entreprise: '',
    date_debut_contrat: '',
    date_fin_contrat: '',
    salaire_base: 0,
    devise: 'CDF',
    banque: '',
    compte_bancaire: '',
    numero_cnss: '',
    numero_inpp: '',
    numero_nif: '',
    piece_identite: '',
    numero_piece: '',
    est_actif: 1,
    observation: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const resetForm = () => {
    setFormData({
      nom: '',
      postnom: '',
      prenom: '',
      date_naissance: '',
      lieu_naissance: '',
      nationalite: 'Congolaise',
      sexe: '',
      etat_civil: 'celibataire',
      nombre_enfants: 0,
      telephone: '',
      telephone2: '',
      email: '',
      province: '',
      ville: '',
      commune: '',
      quartier: '',
      adresse: '',
      photo: 'default.png',
      personne_urgence: '',
      telephone_urgence: '',
      lien_parente: '',
      date_embauche: '',
      date_depart: '',
      service: '',
      departement: '',
      fonction: '',
      categorie_professionnelle: '',
      niveau_etude: '',
      type_contrat: 'CDI',
      entreprise: '',
      date_debut_contrat: '',
      date_fin_contrat: '',
      salaire_base: 0,
      devise: 'USD',
      banque: '',
      compte_bancaire: '',
      numero_cnss: '',
      numero_inpp: '',
      numero_nif: '',
      piece_identite: '',
      numero_piece: '',
      est_actif: 1,
      observation: ''
    });
    setSelectedFile(null);
    setPreviewUrl(null);
    setErrors({});
  };

  useEffect(() => {
    if (employeId && show) {
      const fetchEmploye = async () => {
        setLoading(true);
        try {
          const res = await fetch(`${API_URL}?action=single&id=${employeId}`);
          const data = await res.json();
          if (data.status === 'success') {
            setFormData({
              ...data.data,
              date_naissance: data.data.date_naissance || '',
              date_embauche: data.data.date_embauche || '',
              date_depart: data.data.date_depart || '',
              date_debut_contrat: data.data.date_debut_contrat || '',
              date_fin_contrat: data.data.date_fin_contrat || '',
              salaire_base: parseFloat(data.data.salaire_base) || 0,
              nombre_enfants: parseInt(data.data.nombre_enfants) || 0,
              est_actif: data.data.est_actif !== undefined ? data.data.est_actif : 1
            });
            if (data.data.photo && data.data.photo !== 'default.png') {
              const photoUrl = `${API_CONFIG2.apiUrl}${data.data.photo}`;
              setPreviewUrl(photoUrl);
            }
          } else {
            throw new Error(data.message || 'Erreur');
          }
        } catch (err) {
          setToast({ show: true, message: err.message, variant: 'danger' });
        } finally {
          setLoading(false);
        }
      };
      fetchEmploye();
    } else if (initialData && show && !employeId) {
      // Pré-remplir avec les données du candidat + entreprise + type_contrat
      setFormData(prev => ({
        ...prev,
        nom: initialData.nom || '',
        prenom: initialData.prenom || '',
        postnom: initialData.postnom || '',
        date_naissance: initialData.date_naissance || '',
        telephone: initialData.telephone || '',
        email: initialData.email || '',
        adresse: initialData.adresse || '',
        ville: initialData.ville || '',
        sexe: initialData.sexe || '',
        type_contrat: initialData.type_contrat || prev.type_contrat,
        entreprise: initialData.entreprise || '',
        id: initialData.id || '',
      }));
    } else if (!employeId && !initialData && show) {
      resetForm();
    }
  }, [employeId, initialData, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setPreviewUrl(null);
    }
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData(prev => ({ ...prev, photo: 'default.png' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Nom requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Prénom requis';
    if (!formData.date_embauche) newErrors.date_embauche = "Date d'embauche requise";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (formData.telephone && !/^[0-9+\-\s()]{8,20}$/.test(formData.telephone)) {
      newErrors.telephone = 'Téléphone invalide';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const action = employeId ? 'update' : 'create';
      const body = new FormData();

      // Ajouter tous les champs du formulaire
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          body.append(key, formData[key]);
        }
      });

      if (selectedFile) body.append('photo', selectedFile);
      if (employeId) {
        body.append('_method', 'PUT');
        body.append('id', employeId);
      }

      const res = await fetch(`${API_URL}?action=${action}`, {
        method: 'POST',
        body: body
      });
      const data = await res.json();

      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        if (onSuccess) onSuccess(data.data);
        onHide();
        resetForm();
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setToast({ show: true, message: err.message, variant: 'danger' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
          /* ================================================================
             DESIGN GLASSMORPHISME PREMIUM – EmployeFormModal
             ================================================================ */

          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .modal-glass .modal-content {
            background: rgba(8, 14, 30, 0.85);
            backdrop-filter: blur(40px) saturate(1.2);
            -webkit-backdrop-filter: blur(40px) saturate(1.2);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 32px;
            color: #f8fafc;
            box-shadow: 0 32px 80px rgba(0,0,0,0.7);
            overflow: hidden;
          }

          .modal-glass .modal-header {
            border-bottom: 1px solid rgba(255,255,255,0.04);
            padding: 1.5rem 1.8rem;
          }

          .modal-glass .modal-header .btn-close {
            filter: invert(1) brightness(200%);
            opacity: 0.5;
            transition: opacity 0.3s;
          }
          .modal-glass .modal-header .btn-close:hover {
            opacity: 0.9;
          }

          .modal-glass .modal-footer {
            border-top: 1px solid rgba(255,255,255,0.04);
            padding: 1.2rem 1.8rem;
          }

          .modal-glass .modal-body {
            padding: 1.8rem;
            max-height: 70vh;
            overflow-y: auto;
          }

          /* Scrollbar personnalisée */
          .modal-glass .modal-body::-webkit-scrollbar {
            width: 6px;
          }
          .modal-glass .modal-body::-webkit-scrollbar-track {
            background: rgba(255,255,255,0.02);
            border-radius: 10px;
          }
          .modal-glass .modal-body::-webkit-scrollbar-thumb {
            background: #3b82f6;
            border-radius: 10px;
          }

          .modal-glass .form-section-title {
            font-size: 0.85rem;
            font-weight: 700;
            color: #06b6d4;
            letter-spacing: 0.5px;
            border-bottom: 1px solid rgba(255,255,255,0.04);
            padding-bottom: 8px;
            margin: 1.8rem 0 1.2rem;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .modal-glass .form-section-title .icon {
            opacity: 0.7;
          }

          .modal-glass .form-label {
            font-size: 0.8rem;
            color: #94a3b8;
            font-weight: 500;
            margin-bottom: 4px;
          }

          .modal-glass .form-control,
          .modal-glass .form-select {
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(255,255,255,0.06);
            color: #f8fafc;
            border-radius: 14px;
            padding: 12px 16px;
            transition: all 0.3s ease;
          }

          .modal-glass .form-control:focus,
          .modal-glass .form-select:focus {
            background: rgba(255,255,255,0.06);
            border-color: #3b82f6;
            box-shadow: 0 0 0 4px rgba(59,130,246,0.08);
            color: #f8fafc;
          }

          .modal-glass .form-control::placeholder {
            color: #64748b;
          }

          .modal-glass .form-select option {
            background: #0f172a;
            color: #f8fafc;
          }

          .modal-glass .form-control.is-invalid,
          .modal-glass .form-select.is-invalid {
            border-color: #ef4444;
            box-shadow: 0 0 0 3px rgba(239,68,68,0.08);
          }

          .modal-glass .invalid-feedback {
            color: #f87171;
            font-size: 0.8rem;
          }

          /* Photo Upload */
          .photo-upload-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 14px;
          }

          .photo-preview-container {
            width: 160px;
            height: 160px;
            border-radius: 28px;
            overflow: hidden;
            border: 2px solid rgba(255,255,255,0.06);
            background: #1e293b;
            box-shadow: 0 8px 30px rgba(0,0,0,0.4);
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .photo-preview-container:hover {
            transform: scale(1.02);
            border-color: #3b82f6;
          }

          .photo-preview-container img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .photo-preview-container .placeholder {
            color: #64748b;
            font-size: 3rem;
          }

          .photo-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: center;
          }

          .photo-actions .btn-file {
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.06);
            color: #f8fafc;
            border-radius: 40px;
            padding: 8px 20px;
            font-size: 0.8rem;
            cursor: pointer;
            transition: all 0.3s;
          }

          .photo-actions .btn-file:hover {
            background: rgba(255,255,255,0.08);
            border-color: #3b82f6;
          }

          .photo-actions .btn-remove {
            background: rgba(239,68,68,0.12);
            border: 1px solid rgba(239,68,68,0.15);
            color: #f87171;
            border-radius: 40px;
            padding: 8px 20px;
            font-size: 0.8rem;
            cursor: pointer;
            transition: all 0.3s;
          }

          .photo-actions .btn-remove:hover {
            background: rgba(239,68,68,0.25);
          }

          /* Boutons */
          .btn-glow {
            background: rgba(255,255,255,0.04);
            backdrop-filter: blur(12px);
            border: 1px solid rgba(255,255,255,0.06);
            color: #f8fafc;
            transition: all 0.3s ease;
            border-radius: 40px;
            padding: 10px 24px;
            font-weight: 500;
          }

          .btn-glow:hover {
            background: rgba(255,255,255,0.08);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.3);
          }

          .btn-primary-glow {
            background: #3b82f6;
            border: none;
            color: white;
            box-shadow: 0 4px 20px rgba(59,130,246,0.3);
          }

          .btn-primary-glow:hover {
            background: #1d4ed8;
            box-shadow: 0 8px 30px rgba(59,130,246,0.4);
            transform: translateY(-2px);
          }

          .btn-danger-glow {
            background: rgba(239,68,68,0.12);
            border: 1px solid rgba(239,68,68,0.15);
            color: #f87171;
          }

          .btn-danger-glow:hover {
            background: rgba(239,68,68,0.25);
            transform: translateY(-2px);
          }

          /* Toast */
          .toast-container {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 9999;
            display: flex;
            flex-direction: column;
            gap: 10px;
            max-width: 400px;
          }

          .toast-item {
            background: rgba(8, 14, 30, 0.85);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 20px;
            padding: 14px 20px;
            color: #f8fafc;
            display: flex;
            align-items: center;
            gap: 14px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.4);
            animation: slideIn 0.3s ease;
          }

          .toast-item.success { border-left: 4px solid #3b82f6; }
          .toast-item.danger { border-left: 4px solid #ef4444; }

          @keyframes slideIn {
            from { opacity: 0; transform: translateX(40px); }
            to { opacity: 1; transform: translateX(0); }
          }

          .btn-close-custom {
            background: transparent;
            border: none;
            color: #94a3b8;
            cursor: pointer;
            font-size: 1.2rem;
            margin-left: auto;
            transition: color 0.2s;
          }

          .btn-close-custom:hover {
            color: #f8fafc;
          }

          /* Responsive */
          @media (max-width: 768px) {
            .modal-glass .modal-body {
              padding: 1rem;
            }
            .photo-preview-container {
              width: 120px;
              height: 120px;
            }
          }
        `}
      </style>

      <Modal show={show} onHide={onHide} centered className="modal-glass" size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
            {employeId ? '✏️ Modifier le collaborateur' : '➕ Nouveau collaborateur'}
            {employeId && ` (ID #${employeId})`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 text-muted">Chargement des données...</p>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              {/* Photo */}
              <div className="row mb-4">
                <div className="col-md-3 text-center">
                  <div className="photo-upload-wrapper">
                    <div className="photo-preview-container">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Aperçu" />
                      ) : (
                        <div className="placeholder">
                          <FontAwesomeIcon icon={faImage} />
                        </div>
                      )}
                    </div>
                    <div className="photo-actions">
                      <label className="btn-file">
                        <FontAwesomeIcon icon={faImage} className="me-2" />
                        Choisir
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                          disabled={readOnly}
                        />
                      </label>
                      {previewUrl && !readOnly && (
                        <button type="button" className="btn-remove" onClick={handleRemovePhoto}>
                          <FontAwesomeIcon icon={faTimes} className="me-1" /> Retirer
                        </button>
                      )}
                    </div>
                    {formData.photo && formData.photo !== 'default.png' && !previewUrl && (
                      <span className="text-secondary small">Photo actuelle: {formData.photo}</span>
                    )}
                  </div>
                </div>
                <div className="col-md-9">
                  {/* Section 1 : Identité */}
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faUser} className="icon" /> IDENTITÉ & ÉTAT CIVIL
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        isInvalid={!!errors.nom}
                        disabled={readOnly}
                        placeholder="Nom de famille"
                      />
                      <Form.Control.Feedback type="invalid">{errors.nom}</Form.Control.Feedback>
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Postnom</Form.Label>
                      <Form.Control
                        type="text"
                        name="postnom"
                        value={formData.postnom}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Postnom"
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        isInvalid={!!errors.prenom}
                        disabled={readOnly}
                        placeholder="Prénom"
                      />
                      <Form.Control.Feedback type="invalid">{errors.prenom}</Form.Control.Feedback>
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Date de naissance</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_naissance"
                        value={formData.date_naissance}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Lieu de naissance</Form.Label>
                      <Form.Control
                        type="text"
                        name="lieu_naissance"
                        value={formData.lieu_naissance}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Ville, pays"
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Nationalité</Form.Label>
                      <Form.Control
                        type="text"
                        name="nationalite"
                        value={formData.nationalite}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Sexe</Form.Label>
                      <Form.Select
                        name="sexe"
                        value={formData.sexe || ''}
                        onChange={handleChange}
                        disabled={readOnly}
                      >
                        <option value="">Non spécifié</option>
                        <option value="M">Masculin</option>
                        <option value="F">Féminin</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-4">
                      <Form.Label>État civil</Form.Label>
                      <Form.Select
                        name="etat_civil"
                        value={formData.etat_civil}
                        onChange={handleChange}
                        disabled={readOnly}
                      >
                        <option value="celibataire">Célibataire</option>
                        <option value="marie">Marié(e)</option>
                        <option value="divorce">Divorcé(e)</option>
                        <option value="veuf">Veuf/Veuve</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Nombre d'enfants</Form.Label>
                      <Form.Control
                        type="number"
                        name="nombre_enfants"
                        value={formData.nombre_enfants}
                        onChange={handleChange}
                        min="0"
                        disabled={readOnly}
                      />
                    </div>
                  </div>

                  {/* Section 2 : Contact */}
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faAddressCard} className="icon" /> COORDONNÉES & ADRESSE
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Label>Téléphone</Form.Label>
                      <Form.Control
                        type="text"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        isInvalid={!!errors.telephone}
                        disabled={readOnly}
                        placeholder="+243 81 234 5678"
                      />
                      <Form.Control.Feedback type="invalid">{errors.telephone}</Form.Control.Feedback>
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Téléphone 2</Form.Label>
                      <Form.Control
                        type="text"
                        name="telephone2"
                        value={formData.telephone2}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Téléphone secondaire"
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        disabled={readOnly}
                        placeholder="nom@domaine.com"
                      />
                      <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Province</Form.Label>
                      <Form.Control
                        type="text"
                        name="province"
                        value={formData.province}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Ville</Form.Label>
                      <Form.Control
                        type="text"
                        name="ville"
                        value={formData.ville}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Commune</Form.Label>
                      <Form.Control
                        type="text"
                        name="commune"
                        value={formData.commune}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Quartier</Form.Label>
                      <Form.Control
                        type="text"
                        name="quartier"
                        value={formData.quartier}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-12">
                      <Form.Label>Adresse complète</Form.Label>
                      <Form.Control
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Numéro, rue, bâtiment..."
                      />
                    </div>
                  </div>

                  {/* Section 3 : Urgence */}
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faStethoscope} className="icon" /> CONTACT D'URGENCE
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Label>Personne à contacter</Form.Label>
                      <Form.Control
                        type="text"
                        name="personne_urgence"
                        value={formData.personne_urgence}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Nom complet"
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Téléphone urgence</Form.Label>
                      <Form.Control
                        type="text"
                        name="telephone_urgence"
                        value={formData.telephone_urgence}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Numéro d'urgence"
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Lien de parenté</Form.Label>
                      <Form.Control
                        type="text"
                        name="lien_parente"
                        value={formData.lien_parente}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Conjoint, parent, ami..."
                      />
                    </div>
                  </div>

                  {/* Section 4 : Profession */}
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faBriefcase} className="icon" /> INFORMATIONS PROFESSIONNELLES
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Label>Date d'embauche <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        name="date_embauche"
                        value={formData.date_embauche}
                        onChange={handleChange}
                        isInvalid={!!errors.date_embauche}
                        disabled={readOnly}
                      />
                      <Form.Control.Feedback type="invalid">{errors.date_embauche}</Form.Control.Feedback>
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Date de départ</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_depart"
                        value={formData.date_depart}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Type de contrat</Form.Label>
                      <Form.Select
                        name="type_contrat"
                        value={formData.type_contrat}
                        onChange={handleChange}
                        disabled={readOnly}
                      >
                        <option value="CDI">CDI</option>
                        <option value="CDD">CDD</option>
                        <option value="Stage">Stage</option>
                        <option value="Apprentissage">Apprentissage</option>
                        <option value="Consultant">Consultant</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Service</Form.Label>
                      <Form.Control
                        type="text"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Service / département"
                      />
                    </div>
                    <div className="col-md-4">
                     <Form.Label>Département</Form.Label>
<Form.Select
  name="departement"
  value={formData.departement || ''}
  onChange={handleChange}
  disabled={readOnly}
>
  <option value="">Sélectionner un département</option>
  <option value="Interne">Interne</option>
  <option value="Externe">Externe</option>
</Form.Select>
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Fonction</Form.Label>
                      <Form.Control
                        type="text"
                        name="fonction"
                        value={formData.fonction}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Intitulé du poste"
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Catégorie professionnelle</Form.Label>
                      <Form.Control
                        type="text"
                        name="categorie_professionnelle"
                        value={formData.categorie_professionnelle}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Cadre, Agent, etc."
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Niveau d'étude</Form.Label>
                      <Form.Control
                        type="text"
                        name="niveau_etude"
                        value={formData.niveau_etude}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Bac+5, Master, etc."
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Date début contrat</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_debut_contrat"
                        value={formData.date_debut_contrat}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Date fin contrat</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_fin_contrat"
                        value={formData.date_fin_contrat}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Est actif</Form.Label>
                      <Form.Check
                        type="checkbox"
                        name="est_actif"
                        checked={formData.est_actif === 1}
                        onChange={(e) => setFormData(prev => ({ ...prev, est_actif: e.target.checked ? 1 : 0 }))}
                        disabled={readOnly}
                        label="Employé actif"
                        style={{ marginTop: '8px' }}
                      />
                    </div>
                  </div>

                  {/* Section 5 : Salaire et Banque */}
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faMoneyBillWave} className="icon" /> RÉMUNÉRATION & BANQUE
                  </div>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <Form.Label>Salaire de base</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="salaire_base"
                        value={formData.salaire_base}
                        onChange={handleChange}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Devise</Form.Label>
                      <Form.Select
                        name="devise"
                        value={formData.devise}
                        onChange={handleChange}
                        disabled={readOnly}
                      >
                        <option value="CDF">CDF</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </Form.Select>
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Banque</Form.Label>
                      <Form.Control
                        type="text"
                        name="banque"
                        value={formData.banque}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Nom de la banque"
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Compte bancaire</Form.Label>
                      <Form.Control
                        type="text"
                        name="compte_bancaire"
                        value={formData.compte_bancaire}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Numéro de compte"
                      />
                    </div>
                  </div>

                  {/* Section 6 : Documents */}
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faIdCard} className="icon" /> DOCUMENTS & NUMÉROS OFFICIELS
                  </div>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <Form.Label>N° CNSS</Form.Label>
                      <Form.Control
                        type="text"
                        name="numero_cnss"
                        value={formData.numero_cnss}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="CNSS"
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>N° INPP</Form.Label>
                      <Form.Control
                        type="text"
                        name="numero_inpp"
                        value={formData.numero_inpp}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="INPP"
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>N° NIF</Form.Label>
                      <Form.Control
                        type="text"
                        name="numero_nif"
                        value={formData.numero_nif}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="NIF"
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Pièce d'identité</Form.Label>
                      <Form.Control
                        type="text"
                        name="piece_identite"
                        value={formData.piece_identite}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="CIN, Passeport..."
                      />
                    </div>
                    <div className="col-md-3">
                      <Form.Label>Numéro de pièce</Form.Label>
                      <Form.Control
                        type="text"
                        name="numero_piece"
                        value={formData.numero_piece}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Numéro du document"
                      />
                    </div>
                  </div>

                  {/* Section 7 : Observations */}
                  <div className="form-section-title">
                    <FontAwesomeIcon icon={faStethoscope} className="icon" /> OBSERVATIONS
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <Form.Control
                        as="textarea"
                        rows="3"
                        name="observation"
                        value={formData.observation}
                        onChange={handleChange}
                        disabled={readOnly}
                        placeholder="Notes, remarques particulières..."
                      />
                    </div>
                  </div>

                  {!readOnly && (
                    <div className="d-flex justify-content-end gap-3 mt-4">
                      <Button
                        variant="outline-light"
                        className="btn-glow"
                        onClick={onHide}
                        disabled={submitting}
                      >
                        Annuler
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        className="btn-primary-glow"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <><FontAwesomeIcon icon={faSpinner} spin /> Enregistrement...</>
                        ) : (
                          employeId ? 'Mettre à jour' : 'Ajouter'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* Toast Notification */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeFormModal;